/**
 * useTelephonyClient — subscribe-only hook over the telephony library.
 *
 * The hook owns NO lifecycle and is safe to call from any component. It reads
 * the uniform cross-tab mirror in `phone-state` (owner-projected, follower-fed)
 * so it behaves identically regardless of which tab owns the connection. The
 * action functions are leader-aware in `client.ts`: on a follower they proxy to
 * the owner over the phone-bus.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  dial as libDial,
  answer as libAnswer,
  reject as libReject,
  hangup as libHangup,
  toggleMute as libToggle,
  clearDisposition as libClearDisposition,
  phoneState,
  type CallRecord,
  type ClientCallId,
  type PhoneStatus,
  type Brand,
  type CallerInfo,
  type DialContext,
} from '../lib/telephony';

interface PhoneSnap {
  status: PhoneStatus;
  calls: CallRecord[];
  muted: boolean;
  dispositionCall: CallRecord | null;
}

function readSnap(): PhoneSnap {
  return {
    status: phoneState.getStatus(),
    calls: phoneState.getCalls(),
    muted: phoneState.getMuted(),
    dispositionCall: phoneState.getDisposition(),
  };
}

function pickActiveCall(calls: CallRecord[]): CallRecord | null {
  const live = calls.filter((c) => c.state !== 'ended');
  if (live.length === 0) return null;
  return [...live].sort((a, b) => b.startedAt.getTime() - a.startedAt.getTime())[0] ?? null;
}

export function useTelephonyClient() {
  const [snap, setSnap] = useState<PhoneSnap>(readSnap);

  useEffect(() => {
    const apply = () => setSnap(readSnap());
    apply();
    return phoneState.subscribe(apply);
  }, []);

  const activeCall = pickActiveCall(snap.calls);

  // Keep the active call in a ref so the action callbacks stay stable across
  // renders (avoids reinstalling keyboard shortcuts on every snapshot).
  const activeCallRef = useRef<CallRecord | null>(activeCall);
  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  const dial = useCallback(
    (number: string, brand: Brand | string, customer?: CallerInfo, context?: DialContext | null): Promise<ClientCallId> =>
      libDial(number, brand, customer, context ?? undefined),
    [],
  );

  const answer = useCallback(() => {
    const c = activeCallRef.current;
    if (c) libAnswer(c.clientCallId);
  }, []);

  const reject = useCallback(() => {
    const c = activeCallRef.current;
    if (c) libReject(c.clientCallId);
  }, []);

  const hangup = useCallback(() => {
    const c = activeCallRef.current;
    if (c) libHangup(c.clientCallId);
  }, []);

  const toggleMute = useCallback(() => {
    const c = activeCallRef.current;
    if (c) libToggle(c.clientCallId);
  }, []);

  const clearDisposition = useCallback(() => libClearDisposition(), []);

  return {
    status: snap.status,
    activeCall,
    isMuted: snap.muted,
    dispositionCall: snap.dispositionCall,
    dial,
    answer,
    reject,
    hangup,
    toggleMute,
    clearDisposition,
  };
}
